// components/Layout.jsx (Admin Portal)
import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import { useAuth } from '../context/AuthContext';
import { NotificationPanel } from './NotificationPanel';
import { LicensePermissionModal } from './LicensePermissionModal';

// ============ DESIGN TOKENS ============
const tokens = {
  colors: {
    primary: {
      50: '#fdf4ff',
      100: '#fae8ff',
      200: '#f5d0fe',
      300: '#f0abfc',
      400: '#e879f9',
      500: '#d946ef',
      600: '#c026d3',
      700: '#a21caf',
      800: '#86198f',
      900: '#701a75',
    },
    neutral: {
      0: '#ffffff',
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
      950: '#020617',
    },
    success: { light: '#ecfdf5', main: '#10b981', dark: '#059669' },
    warning: { light: '#fffbeb', main: '#f59e0b', dark: '#d97706' },
    error: { light: '#fef2f2', main: '#ef4444', dark: '#dc2626' },
    accent: {
      purple: '#8b5cf6',
      pink: '#ec4899',
      indigo: '#6366f1',
      cyan: '#06b6d4',
      emerald: '#10b981',
      amber: '#f59e0b',
    },
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
    glow: '0 0 40px rgba(139, 92, 246, 0.15)',
  },
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
    spring: '500ms cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  radius: {
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    '2xl': '24px',
    full: '9999px',
  },
};

// ============ ANIMATIONS ============
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-16px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ============ ICON COMPONENTS ============
const Icons = {
  Dashboard: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  ),
  Users: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  Results: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10,9 9,9 8,9" />
    </svg>
  ),
  Money: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  Email: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  Send: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 2L11 13" />
      <path d="M22 2L15 22L11 13L2 9L22 2Z" />
    </svg>
  ),
  ChangeRequest: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  ),
  Logout: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16,17 21,12 16,7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  Menu: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
  ChevronLeft: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="15,18 9,12 15,6" />
    </svg>
  ),
  Bell: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  Settings: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  Search: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  ),
};

// ============ STYLED COMPONENTS ============
const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${tokens.colors.neutral[50]};
`;

const SidebarWrapper = styled.aside`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: ${props => props.$collapsed ? '72px' : '260px'};
  background: linear-gradient(180deg, ${tokens.colors.neutral[900]} 0%, ${tokens.colors.neutral[950]} 100%);
  display: flex;
  flex-direction: column;
  z-index: 100;
  transition: width ${tokens.transitions.slow};

  @media (max-width: 1024px) {
    transform: translateX(${props => props.$mobileOpen ? '0' : '-100%'});
    width: 280px;
    box-shadow: ${props => props.$mobileOpen ? tokens.shadows.xl : 'none'};
  }
`;

const SidebarBackdrop = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.5);
    backdrop-filter: blur(4px);
    z-index: 90;
    opacity: ${props => props.$visible ? 1 : 0};
    pointer-events: ${props => props.$visible ? 'auto' : 'none'};
    transition: opacity ${tokens.transitions.normal};
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${props => props.$collapsed ? 'center' : 'space-between'};
  padding: ${tokens.spacing?.['2xl'] || '24px'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  min-height: 72px;
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  overflow: hidden;
`;

const LogoIcon = styled.div`
  width: 38px;
  height: 38px;
  background: linear-gradient(135deg, ${tokens.colors.accent.purple} 0%, ${tokens.colors.accent.pink} 100%);
  border-radius: ${tokens.radius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${tokens.radius.lg};
  }
`;

const LogoText = styled.div`
  opacity: ${props => props.$collapsed ? 0 : 1};
  width: ${props => props.$collapsed ? 0 : 'auto'};
  overflow: hidden;
  white-space: nowrap;
  transition: all ${tokens.transitions.slow};

  h1 {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${tokens.colors.neutral[0]};
    margin: 0;
    line-height: 1.2;
  }

  span {
    font-size: 0.7rem;
    color: ${tokens.colors.accent.purple};
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const CollapseToggle = styled.button`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${tokens.colors.neutral[800]};
  border: none;
  border-radius: ${tokens.radius.md};
  color: ${tokens.colors.neutral[400]};
  cursor: pointer;
  transition: all ${tokens.transitions.fast};
  flex-shrink: 0;

  &:hover {
    background: ${tokens.colors.neutral[700]};
    color: ${tokens.colors.neutral[0]};
  }

  svg {
    transition: transform ${tokens.transitions.normal};
    transform: rotate(${props => props.$collapsed ? '180deg' : '0deg'});
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 12px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const NavSection = styled.div`
  margin-bottom: 24px;
`;

const NavLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 600;
  color: ${tokens.colors.neutral[500]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0 12px;
  margin-bottom: 8px;
  opacity: ${props => props.$collapsed ? 0 : 1};
  height: ${props => props.$collapsed ? 0 : 'auto'};
  overflow: hidden;
  transition: all ${tokens.transitions.slow};
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin: 4px 0;
  border-radius: ${tokens.radius.lg};
  color: ${tokens.colors.neutral[400]};
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all ${tokens.transitions.fast};
  position: relative;
  justify-content: ${props => props.$collapsed ? 'center' : 'flex-start'};

  .nav-icon {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: color ${tokens.transitions.fast};
  }

  .nav-text {
    opacity: ${props => props.$collapsed ? 0 : 1};
    width: ${props => props.$collapsed ? 0 : 'auto'};
    overflow: hidden;
    white-space: nowrap;
    transition: all ${tokens.transitions.slow};
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: ${tokens.colors.neutral[100]};
  }

  &.active {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(236, 72, 153, 0.1) 100%);
    color: ${tokens.colors.accent.purple};

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 60%;
      background: linear-gradient(180deg, ${tokens.colors.accent.purple}, ${tokens.colors.accent.pink});
      border-radius: 0 2px 2px 0;
    }
  }

  /* Tooltip for collapsed state */
  &::after {
    content: attr(data-tooltip);
    position: absolute;
    left: 100%;
    margin-left: 12px;
    padding: 6px 12px;
    background: ${tokens.colors.neutral[800]};
    color: ${tokens.colors.neutral[100]};
    font-size: 0.8rem;
    border-radius: ${tokens.radius.md};
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity ${tokens.transitions.fast};
    box-shadow: ${tokens.shadows.lg};
    z-index: 100;
  }

  &:hover::after {
    opacity: ${props => props.$collapsed ? 1 : 0};
  }
`;

const SidebarFooter = styled.div`
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  margin-top: auto;
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: ${tokens.radius.lg};
  background: rgba(255, 255, 255, 0.03);
  margin-bottom: 12px;
  justify-content: ${props => props.$collapsed ? 'center' : 'flex-start'};
  transition: background ${tokens.transitions.fast};

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

const Avatar = styled.div`
  width: 38px;
  height: 38px;
  background: linear-gradient(135deg, ${tokens.colors.accent.purple} 0%, ${tokens.colors.accent.pink} 100%);
  border-radius: ${tokens.radius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 0.8rem;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  opacity: ${props => props.$collapsed ? 0 : 1};
  width: ${props => props.$collapsed ? 0 : 'auto'};
  overflow: hidden;
  transition: all ${tokens.transitions.slow};
  min-width: 0;

  .name {
    font-size: 0.85rem;
    font-weight: 600;
    color: ${tokens.colors.neutral[100]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .role {
    font-size: 0.7rem;
    color: ${tokens.colors.accent.purple};
    font-weight: 500;
    text-transform: capitalize;
  }
`;

const LogoutButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${props => props.$collapsed ? 'center' : 'flex-start'};
  gap: 12px;
  padding: 12px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${tokens.radius.lg};
  color: ${tokens.colors.neutral[400]};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${tokens.transitions.fast};

  .logout-text {
    opacity: ${props => props.$collapsed ? 0 : 1};
    width: ${props => props.$collapsed ? 0 : 'auto'};
    overflow: hidden;
    white-space: nowrap;
    transition: all ${tokens.transitions.slow};
  }

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 0.3);
    color: ${tokens.colors.error.main};
  }
`;

const MainWrapper = styled.main`
  flex: 1;
  margin-left: ${props => props.$collapsed ? '72px' : '260px'};
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  transition: margin-left ${tokens.transitions.slow};

  @media (max-width: 1024px) {
    margin-left: 0;
  }
`;

const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid ${tokens.colors.neutral[200]};
  padding: 0 24px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  @media (max-width: 768px) {
    padding: 0 16px;
    height: 56px;
  }
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MobileMenuButton = styled.button`
  display: none;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  background: ${tokens.colors.neutral[100]};
  border: none;
  border-radius: ${tokens.radius.md};
  color: ${tokens.colors.neutral[700]};
  cursor: pointer;
  transition: all ${tokens.transitions.fast};

  &:hover {
    background: ${tokens.colors.neutral[200]};
  }

  @media (max-width: 1024px) {
    display: flex;
  }
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    display: none;
  }

  span {
    color: ${tokens.colors.neutral[400]};
  }

  a {
    color: ${tokens.colors.neutral[500]};
    text-decoration: none;
    transition: color ${tokens.transitions.fast};

    &:hover {
      color: ${tokens.colors.accent.purple};
    }
  }

  .current {
    color: ${tokens.colors.neutral[800]};
    font-weight: 600;
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${tokens.colors.neutral[100]};
  border: 1px solid transparent;
  border-radius: ${tokens.radius.lg};
  padding: 8px 16px;
  width: 280px;
  transition: all ${tokens.transitions.fast};

  &:focus-within {
    background: ${tokens.colors.neutral[0]};
    border-color: ${tokens.colors.accent.purple};
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
  }

  input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 0.875rem;
    color: ${tokens.colors.neutral[800]};
    outline: none;

    &::placeholder {
      color: ${tokens.colors.neutral[400]};
    }
  }

  svg {
    color: ${tokens.colors.neutral[400]};
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: ${tokens.radius.md};
  color: ${tokens.colors.neutral[500]};
  cursor: pointer;
  transition: all ${tokens.transitions.fast};
  position: relative;

  &:hover {
    background: ${tokens.colors.neutral[100]};
    color: ${tokens.colors.neutral[700]};
  }

  .badge {
    position: absolute;
    top: 4px;
    right: 4px;
    min-width: 18px;
    height: 18px;
    background: ${tokens.colors.error.main};
    border-radius: 50%;
    border: 2px solid white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    font-weight: 700;
    color: white;
  }
`;

const TopBarDivider = styled.div`
  width: 1px;
  height: 24px;
  background: ${tokens.colors.neutral[200]};
  margin: 0 8px;
`;

const TopBarUser = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  border-radius: ${tokens.radius.lg};
  cursor: pointer;
  transition: background ${tokens.transitions.fast};

  &:hover {
    background: ${tokens.colors.neutral[100]};
  }

  @media (max-width: 768px) {
    padding: 6px;
    
    .user-details {
      display: none;
    }
  }
`;

const TopBarAvatar = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, ${tokens.colors.accent.purple} 0%, ${tokens.colors.accent.pink} 100%);
  border-radius: ${tokens.radius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 0.7rem;
`;

const TopBarUserDetails = styled.div`
  .name {
    font-size: 0.8rem;
    font-weight: 600;
    color: ${tokens.colors.neutral[800]};
    line-height: 1.2;
  }

  .role {
    font-size: 0.65rem;
    color: ${tokens.colors.accent.purple};
    font-weight: 500;
    text-transform: capitalize;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
`;

// ============ COMPONENT ============
const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showLicenseModal, setShowLicenseModal] = useState(false);

  // Add notification helper
  const addNotification = useCallback((notification) => {
    const id = Date.now();
    const newNotification = {
      id,
      timestamp: new Date(),
      isRead: false,
      ...notification,
    };
    setNotifications(prev => [newNotification, ...prev]);
    return id;
  }, []);

  // Listen for practitioner form completion notifications
  useEffect(() => {
    const handleFormCompletion = (event) => {
      if (event.key === 'formCompleted') {
        const data = JSON.parse(event.newValue || '{}');
        addNotification({
          type: 'success',
          title: 'Client Assessment Completed',
          message: `${data.clientName} has completed their assessment.`,
          details: {
            'Client Name': data.clientName,
            'Practitioner': data.practitionerName,
            'Completed At': new Date().toLocaleString(),
          },
          actions: [
            {
              label: 'Review Results',
              variant: 'primary',
              onClick: () => navigate('/results'),
            },
          ],
        });
      }
    };

    window.addEventListener('storage', handleFormCompletion);
    return () => window.removeEventListener('storage', handleFormCompletion);
  }, [navigate, addNotification]);

  // Listen for change request approvals
  useEffect(() => {
    const handleChangeRequestApproval = (event) => {
      if (event.key === 'changeRequestApproved') {
        const data = JSON.parse(event.newValue || '{}');
        addNotification({
          type: 'info',
          title: 'Change Request Status',
          message: `You have approved the change request from ${data.practitionerName}.`,
          details: {
            'Practitioner': data.practitionerName,
            'Change Type': data.changeType || 'Profile Update',
            'Timestamp': new Date().toLocaleString(),
          },
        });
      }
    };

    window.addEventListener('storage', handleChangeRequestApproval);
    return () => window.removeEventListener('storage', handleChangeRequestApproval);
  }, [addNotification]);

  // Listen for license renewal requests
  useEffect(() => {
    const handleLicenseRenewalRequest = (event) => {
      if (event.key === 'licenseRenewalRequest') {
        const data = JSON.parse(event.newValue || '{}');
        addNotification({
          type: 'warning',
          title: 'License Renewal Request',
          message: `${data.practitionerName} has submitted a license renewal request.`,
          details: {
            'Practitioner': data.practitionerName,
            'License Expires': new Date(data.licenseExpiry).toLocaleDateString(),
            'Status': data.reason || 'Pending Review',
          },
          actions: [
            {
              label: 'Review Request',
              variant: 'primary',
              onClick: () => navigate('/change-requests'),
            },
          ],
        });
      }
    };

    window.addEventListener('storage', handleLicenseRenewalRequest);
    return () => window.removeEventListener('storage', handleLicenseRenewalRequest);
  }, [navigate, addNotification]);

  // Listen for license expiry warnings for practitioners
  useEffect(() => {
    const handleLicenseExpiry = (event) => {
      if (event.key === 'practitionerLicenseExpiring') {
        const data = JSON.parse(event.newValue || '{}');
        addNotification({
          type: 'warning',
          title: 'Practitioner License Expiring',
          message: `${data.practitionerName}'s license will expire soon.`,
          details: {
            'Practitioner': data.practitionerName,
            'Expiration Date': new Date(data.licenseExpiry).toLocaleDateString(),
            'Days Remaining': data.daysLeft,
          },
          actions: [
            {
              label: 'View Practitioner',
              variant: 'primary',
              onClick: () => navigate('/practitioners'),
            },
          ],
        });
      }
    };

    window.addEventListener('storage', handleLicenseExpiry);
    return () => window.removeEventListener('storage', handleLicenseExpiry);
  }, [navigate, addNotification]);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsMobileOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const getPageTitle = useCallback(() => {
    const path = location.pathname;
    const routes = {
      '/dashboard': 'Dashboard',
      '/practitioners': 'Practitioners',
      '/change-requests': 'Change Requests',
      '/results': 'All Results',
      '/payouts': 'Payouts',
      '/emails': 'Emails',
    };
    return routes[path] || 'Dashboard';
  }, [location.pathname]);

  const navItems = [
    { path: '/dashboard', icon: Icons.Dashboard, label: 'Dashboard' },
    { path: '/practitioners', icon: Icons.Users, label: 'Practitioners' },
    { path: '/change-requests', icon: Icons.ChangeRequest, label: 'Change Requests' },
    { path: '/results', icon: Icons.Results, label: 'All Results' },
    { path: '/payouts', icon: Icons.Money, label: 'Payouts' },
    { path: '/emails', icon: Icons.Email, label: 'Emails' },
  ];

  const userInitials = `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`;

  return (
    <LayoutContainer>
      <SidebarBackdrop
        $visible={isMobileOpen}
        onClick={() => setIsMobileOpen(false)}
        aria-hidden="true"
      />

      <SidebarWrapper
        $collapsed={isCollapsed}
        $mobileOpen={isMobileOpen}
        role="navigation"
        aria-label="Admin navigation"
      >
        <SidebarHeader $collapsed={isCollapsed}>
          <LogoWrapper>
            <LogoIcon>
              <img src="/logo192.png" alt="Visioning Assessment" />
            </LogoIcon>
            <LogoText $collapsed={isCollapsed}>
              <h1>Visioning Assessment</h1>
              <span>Admin Portal</span>
            </LogoText>
          </LogoWrapper>
          {!isCollapsed && (
            <CollapseToggle
              $collapsed={isCollapsed}
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-label="Collapse sidebar"
            >
              <Icons.ChevronLeft />
            </CollapseToggle>
          )}
        </SidebarHeader>

        {isCollapsed && (
          <div style={{ padding: '12px', display: 'flex', justifyContent: 'center' }}>
            <CollapseToggle
              $collapsed={isCollapsed}
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-label="Expand sidebar"
            >
              <Icons.ChevronLeft />
            </CollapseToggle>
          </div>
        )}

        <SidebarContent>
          <NavSection>
            <NavLabel $collapsed={isCollapsed}>Main Menu</NavLabel>
            {navItems.map((item) => (
              <NavItem
                key={item.path}
                to={item.path}
                $collapsed={isCollapsed}
                data-tooltip={item.label}
              >
                <span className="nav-icon">
                  <item.icon />
                </span>
                <span className="nav-text">{item.label}</span>
              </NavItem>
            ))}
          </NavSection>
        </SidebarContent>

        <SidebarFooter>
          <UserCard $collapsed={isCollapsed}>
            <Avatar>{userInitials}</Avatar>
            <UserInfo $collapsed={isCollapsed}>
              <div className="name">{user?.firstName} {user?.lastName}</div>
              <div className="role">{user?.role}</div>
            </UserInfo>
          </UserCard>
          <LogoutButton
            $collapsed={isCollapsed}
            onClick={handleLogout}
            aria-label="Sign out"
          >
            <Icons.Logout />
            <span className="logout-text">Sign Out</span>
          </LogoutButton>
        </SidebarFooter>
      </SidebarWrapper>

      <MainWrapper $collapsed={isCollapsed}>
        <TopBar>
          <TopBarLeft>
            <MobileMenuButton
              onClick={() => setIsMobileOpen(true)}
              aria-label="Open menu"
            >
              <Icons.Menu />
            </MobileMenuButton>

            <Breadcrumb>
              <a href="/dashboard">Admin</a>
              <span>/</span>
              <span className="current">{getPageTitle()}</span>
            </Breadcrumb>

            <SearchBar>
              <Icons.Search />
              <input
                type="text"
                placeholder="Search..."
                aria-label="Search"
              />
            </SearchBar>
          </TopBarLeft>

          <TopBarRight>
            <IconButton 
              aria-label="Notifications"
              onClick={() => setShowNotificationPanel(!showNotificationPanel)}
              title="Notifications"
            >
              <Icons.Bell />
              {notifications.filter(n => !n.isRead).length > 0 && (
                <span className="badge">{notifications.filter(n => !n.isRead).length}</span>
              )}
            </IconButton>
            <IconButton 
              aria-label="Settings"
              onClick={() => navigate('/profile')}
              title="Settings"
            >
              <Icons.Settings />
            </IconButton>

            <TopBarDivider />

            <TopBarUser>
              <TopBarAvatar>{userInitials}</TopBarAvatar>
              <TopBarUserDetails className="user-details">
                <div className="name">{user?.firstName}</div>
                <div className="role">{user?.role}</div>
              </TopBarUserDetails>
            </TopBarUser>
          </TopBarRight>
        </TopBar>

        <ContentArea>
          {children}
        </ContentArea>
      </MainWrapper>

      {/* Notification Panel */}
      <NotificationPanel
        isOpen={showNotificationPanel}
        onClose={() => setShowNotificationPanel(false)}
        notifications={notifications}
      />

      {/* License Permission Modal */}
      <LicensePermissionModal
        isOpen={showLicenseModal}
        onClose={() => setShowLicenseModal(false)}
        onSubmit={() => setShowLicenseModal(false)}
        licenseExpiryDate={new Date()}
      />
    </LayoutContainer>
  );
};

export default Layout;